<template>
  <div class="app" v-if="!isLoading" :key="localeKey" :style="style">
    <div
      class="
        relative
        overflow-x-hidden
        md:flex md:min-h-screen md:items-center md:justify-center
      "
    >
      <div
        class="
          app-wrap
          z-10
          relative
          min-h-screen
          md:min-h-0
          overflow-x-hidden
          overflow-y-scroll
          md:h-80vh
          w-full
          md:rounded-xl md:max-w-4xl
          mx-auto
        "
      >
        <Footer @onLocale="onLocale" />
        <router-view />
      </div>
      <div
        class="
          absolute
          pointer-events-none
          left-0
          top-0
          overflow-hidden
          min-h-full
          h-screen
          w-screen
          flex
          justify-center
          items-center
          z-0
          bg-gray-500
        "
      >
        <img
          class="object-cover h-full w-full opacity-50 filter blur-lg"
          :src="themeBackgroundImageUrl"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { BridgeHandler } from '../mixins/bridge-handler'
import { Component, Mixins, Watch } from 'vue-property-decorator'
import { Getter } from 'vuex-class'
import { AppHandler } from '../mixins/app-handler'
import { TranslationHandler } from '../mixins/translation-handler'
import { JourneyStep } from '@/types'

@Component({
  components: {},
})
export default class App extends Mixins(AppHandler, BridgeHandler, TranslationHandler) {
  @Getter themePrimaryColor!: string
  @Getter themeBackgroundImageUrl!: string
  @Getter currentStep!: JourneyStep | null

  isLoading = true
  localeKey = ''

  // watch on current step changes
  @Watch('currentStep', { immediate: true })
  onCurrentStepChange (step: JourneyStep | null, previousStep: JourneyStep|null) {
    console.log('CURRENT STEP', JSON.stringify(step))
    // Routes that are not part of the step journey and should not be redirected from
    if (this.$route.meta?.noStepRedirect) {
      return
    }

    if (!step) {
      return
    }
    const pushSafely = (location: string) => {
      const newRouteResolved = this.$router.resolve(location)
      if (newRouteResolved.route.fullPath !== this.$route.fullPath) {
        console.log('Navigating to', location)
        this.$router.push(location)
      }
    }

    if (step.name === 'Home') {
      pushSafely('/')
    } else if (step.name === 'Newsletter') {
      pushSafely('/newsletter')
    } else if (step.name === 'Playlist') {
      pushSafely('/breaker/search')
    } else if (step.name === 'Login') {
      pushSafely('/login')
    } else if (step.name === 'Result') {
      pushSafely('/result')
    }
  }

  async created () {
    // This init procedure is somewhat complicated because
    // we're not using CMS to handle auth
    // 1. Init CMS without tracking so campaign, translations, etc is loaded
    try {
      await this.$appInit({ editor: false })
      this.isLoading = false
    } catch (e) {
      alert(e)
      console.log(e)
    }

    if (window.parent) {
      this.$bridgeListenMessages()
    }
  }

  get style () {
    return {
      '--primary-color': this.themePrimaryColor,
    }
  }

  onLocale (locale: string) {
    this.$setLocale(locale)
    this.localeKey = locale
  }
}
</script>

<style>
@media screen and (max-height: 950px) and (orientation: landscape) {
  .app-wrap {
    height: 95vh !important;
  }
}

html {
  font-size: 62.5%;
  line-height: 1.8;
  hyphens: auto;
}

@media screen and (min-height: 640px) {
  html {
    font-size: 67.5%;
  }
}

@media screen and (min-height: 1024px) {
  html {
    font-size: 70%;
  }
}

select {
  background-image: none;
}
</style>
